import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";

import { Button } from "@/components/ui/button";

type StatusPageCardProps = {
  eyebrow: string;
  title: string;
  description: string;
  primaryAction?: {
    href: string;
    label: string;
  };
  secondaryAction?: {
    href: string;
    label: string;
  };
  fullScreen?: boolean;
  children?: ReactNode;
};

export function StatusPageCard({
  eyebrow,
  title,
  description,
  primaryAction,
  secondaryAction,
  fullScreen = true,
  children,
}: StatusPageCardProps) {
  return (
    <section
      className={fullScreen ? "flex min-h-screen items-center bg-background" : "bg-background"}
    >
      <div className="mx-auto w-full max-w-3xl px-6 py-20 md:py-24">
        <div className="overflow-hidden rounded-[2rem] border border-border/70 bg-background p-8 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)] md:p-10">
          <Link href="/" aria-label="Cooperly home" className="inline-flex">
            <Image
              src="/cooperly/cooperly_logo.svg"
              alt="Cooperly logo"
              width={120}
              height={28}
              className="h-7 w-auto"
            />
          </Link>

          <div className="mt-8 space-y-4">
            <p className="site-eyebrow font-semibold uppercase tracking-[0.12em] text-foreground/72">
              {eyebrow}
            </p>
            <h1 className="site-page-title max-w-2xl text-foreground">{title}</h1>
            <p className="site-page-lead max-w-2xl text-foreground/78">
              {description}
            </p>
          </div>

          {(primaryAction || secondaryAction) ? (
            <div className="mt-8 flex flex-wrap gap-3">
              {primaryAction ? (
                <Button asChild size="lg">
                  <Link href={primaryAction.href}>{primaryAction.label}</Link>
                </Button>
              ) : null}
              {secondaryAction ? (
                <Button asChild size="lg" variant="outline">
                  <Link href={secondaryAction.href}>{secondaryAction.label}</Link>
                </Button>
              ) : null}
            </div>
          ) : null}
          {children ? <div className="mt-4 flex flex-wrap gap-3">{children}</div> : null}
        </div>
      </div>
    </section>
  );
}
